import { Box, CardMedia, Grid, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import React from "react";
import { useLocation } from "react-router-dom";
import ActiveIcon from "../common/assets/images/iconStatusActive.svg";
import InactiveIcon from "../common/assets/images/iconStatusInactive.svg";
import OtherIcon from "../common/assets/images/iconStatusOther.svg";
import { fetchData } from "../common/services/ApiClient";
import { formatDate, setInstitutionStatus } from "../common/services/Utilities";
import { ApiBaseUrl, AppUrl } from "../config";
import {
  INSTITUTION_STATUS,
  Query,
  ResultRecord,
  SucceedingInstitution,
} from "../interfaces";
import RouterLink from "./RouterLink";
import { CardListCondensedItem } from "./SearchResultCard/SearchResultCard";

const PREFIX = "ActiveStatus";

const classes = {
  icon: `${PREFIX}-icon`,
  arrowContainer: `${PREFIX}-arrowContainer`,
  arrow: `${PREFIX}-arrow`,
  arrowTail: `${PREFIX}-arrowTail`,
  succeedingInstButton: `${PREFIX}-succeedingInstButton`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.icon}`]: {
    margin: "5px auto",
  },

  [`& .${classes.arrowContainer}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  [`& .${classes.arrow}`]: {
    borderLeft: "55px solid transparent",
    borderRight: "55px solid transparent",
    borderTop: "40px solid #E9EDF2",
  },

  [`& .${classes.arrowTail}`]: {
    width: "50px",
    height: "15px",
    backgroundColor: "#E9EDF2",
  },

  [`& .${classes.succeedingInstButton}`]: {
    "border": "1px solid #D9E7F6",
    "boxShadow": theme.shadows[2],
    "height": "auto",
    "textAlign": "center",
    "cursor": "pointer",
    "transition": ".3s ease",
    "marginBottom": "1rem",
    "marginTop": 0,
    "width": "100%",
    "display": "block",
    "&#succeeding-institution:hover, &#succeeding-institution:focus": {
      boxShadow: theme.shadows[5],
      textDecoration: "none",
    },
  },
}));

interface SucceedingInstDisplayProps {
  succeedingInst: SucceedingInstitution;
  mostRecentStatus: any;
  iconFontSize: number;
}

const SucceedingInstDisplay = ({
  succeedingInst,
  mostRecentStatus,
  iconFontSize,
}: SucceedingInstDisplayProps) => {
  if (succeedingInst) {
    const instToDisplay = succeedingInst.resultData[0];

    return (
      <Box
        sx={{
          marginTop: 1,
        }}
      >
        <Typography
          id="succeeding-institution"
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.main,
          }}
        >
          {instToDisplay.NAME}
        </Typography>
        <Typography
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.main,
          }}
        >
          Cert - {instToDisplay.CERT}
        </Typography>
        <Box
          sx={{
            fontSize: iconFontSize,
          }}
        >
          <CardMedia
            style={{ width: iconFontSize, color: mostRecentStatus.color }}
            className={mostRecentStatus.class}
            component="img"
            alt={mostRecentStatus.altText}
            src={mostRecentStatus.component}
          />
        </Box>
        <Typography
          sx={{
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.main,
          }}
        >
          {mostRecentStatus.altText}
        </Typography>
        <Typography>Since {formatDate(instToDisplay.INSDATE)}</Typography>
        <Typography
          gutterBottom
          sx={{
            fontWeight: "bold",
            marginY: 1,
            color: (theme) => theme.palette.primary.main,
          }}
        >
          Click to View Succeeding Institution
        </Typography>
      </Box>
    );
  } else {
    return <></>;
  }
};

export default function ActiveStatus(props: {
  institution: ResultRecord;
  levelTwo?: boolean;
  appState: Query;
}) {
  const location = useLocation();

  const { institution, levelTwo, appState } = props;
  let closedInst = false;
  let closedInstDate: string;
  let closedInstURL: string;

  if (
    appState.ui.pendingClosedBanks &&
    appState.ui.pendingClosedBanks.some(
      (bank: any) => Number(bank.CERT) === Number(institution.CERT),
    )
  ) {
    closedInst = true;
    const closedBanks = appState.ui.pendingClosedBanks;
    for (const element of closedBanks) {
      if (Number(element.CERT) === Number(institution.CERT)) {
        closedInstDate = element.date;
        closedInstURL = element.url;
      }
    }
  }

  const [succeedingInst, setSucceedingInst] =
    React.useState<SucceedingInstitution>({
      resultData: [],
      display: false,
      primaryRegulator: "",
    });

  if (
    succeedingInst.display &&
    location.state &&
    location.state.from === "succeedingInstitutions"
  ) {
    setSucceedingInst({ ...succeedingInst, display: false });
  }
  React.useEffect(() => {
    if (institution.ACTIVE === 1 && succeedingInst.display) {
      setSucceedingInst({ ...succeedingInst, display: false });
    }
  }, [institution.ACTIVE, succeedingInst]);

  const iconFontSize = levelTwo ? 72 : 64;
  const textFontSize = 16;

  function getInstitutionStatusDescriptions(institution: ResultRecord) {
    const active = {
      component: ActiveIcon,
      altText: "FDIC-Insured",
      color: "#0ca900",
      class: clsx("activeIcon", classes.icon),
      mainDescription: "FDIC-Insured",
      shortDescription: [
        institution.INSDATE ? `Since ${formatDate(institution.INSDATE)}` : "",
      ],
      longDescription: [
        institution.INSDATE ? `Since ${formatDate(institution.INSDATE)}` : "",
      ],
    };
    const inactive = {
      component: InactiveIcon,
      altText: "Inactive Institution",
      color: "#f59a23",
      class: clsx("inactiveIcon", classes.icon),
      mainDescription: "Institution Closed",
      shortDescription: institution.ENDEFYMD
        ? [`as of ${formatDate(institution.ENDEFYMD)}`, "Insured until closed"]
        : [""],
      longDescription: institution.ENDEFYMD
        ? [`as of ${formatDate(institution.ENDEFYMD)}`, "Insured until closed"]
        : [""],
    };
    const statusLookup: { [key in INSTITUTION_STATUS]: any } = {
      OPEN: {
        component: ActiveIcon,
        altText: "FDIC-Insured",
        color: "#0ca900",
        class: clsx("activeIcon", classes.icon),
        mainDescription: "FDIC-Insured",
        shortDescription: [`Since ${formatDate(institution.INSDATE)}`],
        longDescription: [`Since ${formatDate(institution.INSDATE)}`],
      },
      CLOSED_WITH_GOV: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Received Government Assistance",
        shortDescription: [
          `on ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Failed on ${formatDate(institution.ENDEFYMD)}`,
          "and was insured until closed",
        ],
      },
      CLOSED_WITH_GOV_MERGED: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Received Government Assistance",
        shortDescription: [
          `on ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Failed on ${formatDate(institution.ENDEFYMD)}`,
          "and was insured until acquired",
        ],
      },
      CLOSED_WITHOUT_GOV_ACQUIRED_MERGE: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Institution Closed",
        shortDescription: [
          `as of ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Merged or acquired on ${formatDate(institution.ENDEFYMD)}`,
          "without government assistance",
        ],
      },
      CLOSED_WITHOUT_GOV: {
        component: InactiveIcon,
        altText: "Inactive Institution",
        color: "#f59a23",
        class: clsx("inactiveIcon", classes.icon),
        mainDescription: "Institution Closed",
        shortDescription: [
          `as of ${formatDate(institution.ENDEFYMD)}`,
          "Insured until closed",
        ],
        longDescription: [
          `Closed on ${formatDate(institution.ENDEFYMD)} without`,
          "government assistance",
        ],
      },
      RELINQUISHED: {
        component: OtherIcon,
        altText: "No Longer FDIC-Insured",
        color: "#d7d7d7",
        class: clsx("otherIcon", classes.icon),
        mainDescription: "Insurance Relinquished",
        shortDescription: [`as of ${formatDate(institution.INSDROPDATE)}`],
        longDescription: [
          "Voluntarily relinquished FDIC insurance.",
          `Insured until ${formatDate(institution.INSDROPDATE)}`,
        ],
      },
    };

    return closedInst
      ? {
          component: InactiveIcon,
          altText: "Institution Inactive",
          color: "#f59a23",
          class: clsx("inactiveIcon", classes.icon),
          mainDescription: "Institution Inactive",
          shortDescription: [
            `as of ${closedInstDate}`,
            "Additional details available soon",
          ],
          longDescription: [
            "Additional details available soon",
            <a
              href={closedInstURL}
              target="_blank"
              rel="noopener noreferrer"
              key={institution.CERT}
            >
              Press Release
            </a>,
          ],
        }
      : institution.INSTITUTION_STATUS
        ? statusLookup[institution.INSTITUTION_STATUS]
        : institution.ACTIVE === 1
          ? active
          : inactive;
  }

  const status = getInstitutionStatusDescriptions(institution);

  const isFinancialReporting = location.pathname.includes(
    "financialreporting/details",
  );

  React.useEffect(() => {
    if (
      institution?.ULTCERT &&
      levelTwo &&
      institution.ULTCERT.toString() !== institution.CERT.toString()
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/institutions?filters=CERT:${institution.ULTCERT}`,
        )
          .then((response) => {
            if (response.data.length > 0 && isMounted) {
              setSucceedingInst({
                resultData: response.data.map((data: any) =>
                  setInstitutionStatus(data, closedInst),
                ),
                display: true,
                primaryRegulator: response.data[0].REGAGNT,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [closedInst, institution, levelTwo]);

  return levelTwo && succeedingInst.display ? (
    <StyledGrid
      container
      sx={{
        "textAlign": "center",
        "width": "100%",
        "& .MuiGrid-root": {
          width: "100%",
        },
      }}
    >
      <RouterLink
        buttonLink
        id="succeeding-institution"
        className={classes.succeedingInstButton}
        href={`${AppUrl("BankFind")}/details/${
          succeedingInst.resultData[0].CERT
        }`}
        from="succeedingInstitutions"
      >
        <Paper sx={{ padding: 2, width: "100%" }}>
          {isFinancialReporting ? (
            <Box sx={{ flexGrow: 1 }}>
              <Grid
                container
                spacing={3}
                columns={12}
                sx={{
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "stretch",
                  justifyContent: "center",
                  minHeight: 220,
                }}
              >
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  <CardMedia
                    style={{ width: iconFontSize, color: status.color }}
                    className={status.class}
                    component="img"
                    alt={status.altText}
                    src={status.component}
                  />
                  <CardListCondensedItem
                    sx={{
                      "textAlign": "center",
                      "& .MuiGrid-root": { width: "100%" },
                    }}
                    itemLabel={status.mainDescription}
                    itemValue={
                      props.levelTwo
                        ? status.longDescription[0]
                        : status.shortDescription[0]
                    }
                  />
                  {status.shortDescription[1] || status.longDescription[1] ? (
                    <CardListCondensedItem
                      sx={{
                        "textAlign": "center",
                        "& .MuiGrid-root": { width: "100%" },
                      }}
                      itemValueId="active-status-sub-description"
                      itemValue={
                        props.levelTwo
                          ? status.longDescription[1]
                          : status.shortDescription[1]
                      }
                    />
                  ) : undefined}
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                      textAlign: "center",
                      lineHeight: 1.4,
                    }}
                  >
                    See the succeeding institution for more information.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 1,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#E9EDF2",
                        width: { xs: "50px", md: "60px" },
                        height: { xs: "15px", md: "50px" },
                      }}
                    />
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: {
                          xs: "40px solid #E9EDF2",
                          md: "35px solid transparent",
                        },
                        borderBottom: {
                          xs: "none",
                          md: "35px solid transparent",
                        },
                        borderLeft: {
                          xs: "55px solid transparent",
                          md: "45px solid #E9EDF2",
                        },
                        borderRight: {
                          xs: "55px solid transparent",
                          md: "none",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SucceedingInstDisplay
                    succeedingInst={succeedingInst}
                    mostRecentStatus={getInstitutionStatusDescriptions(
                      succeedingInst.resultData[0],
                    )}
                    iconFontSize={iconFontSize}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid>
              <Box sx={{ fontSize: iconFontSize }}>
                <CardMedia
                  style={{ width: iconFontSize, color: status.color }}
                  className={status.class}
                  component="img"
                  alt={status.altText}
                  src={status.component}
                />
              </Box>
              <Typography
                sx={(theme) => ({ color: theme.palette.primary.main })}
                component="div"
              >
                <Box
                  sx={{ fontWeight: "fontWeightBold", fontSize: textFontSize }}
                >
                  <Box className="statusText">
                    <Box sx={{ fontWeight: "inherit", fontSize: "inherited" }}>
                      <CardListCondensedItem
                        sx={{
                          "textAlign": "center",
                          "& .MuiGrid-root": { width: "100%" },
                        }}
                        itemLabel={status.mainDescription}
                        itemValue={
                          props.levelTwo
                            ? status.longDescription[0]
                            : status.shortDescription[0]
                        }
                      />
                      {status.shortDescription[1] ||
                      status.longDescription[1] ? (
                        <CardListCondensedItem
                          sx={{
                            "textAlign": "center",
                            "& .MuiGrid-root": { width: "100%" },
                          }}
                          itemValueId="active-status-sub-description"
                          itemValue={
                            props.levelTwo
                              ? status.longDescription[1]
                              : status.shortDescription[1]
                          }
                        />
                      ) : undefined}
                    </Box>
                    {succeedingInst && (
                      <>
                        <Box sx={{ marginTop: 3 }}>
                          <Typography
                            sx={{
                              marginBottom: -1,
                              color: (theme) => theme.palette.primary.main,
                            }}
                          >
                            See the succeeding institution
                          </Typography>
                          <Typography color="primary.main">
                            for more information.
                          </Typography>
                          <Box className={classes.arrowContainer}>
                            <Box className={classes.arrowTail} />
                            <Box className={classes.arrow} />
                          </Box>
                        </Box>
                        <SucceedingInstDisplay
                          succeedingInst={succeedingInst}
                          mostRecentStatus={getInstitutionStatusDescriptions(
                            succeedingInst.resultData[0],
                          )}
                          iconFontSize={iconFontSize}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Typography>
            </Grid>
          )}
        </Paper>
      </RouterLink>
    </StyledGrid>
  ) : (
    <StyledGrid
      container
      sx={{
        "textAlign": "center",
        "& .MuiGrid-root": {
          width: "100%",
        },
      }}
    >
      <Grid>
        <Box
          sx={{
            fontSize: iconFontSize,
          }}
        >
          <CardMedia
            style={{ width: iconFontSize, color: status.color }}
            className={status.class}
            component="img"
            alt={status.altText}
            src={status.component}
          />
        </Box>
        <Typography
          sx={(theme) => ({ color: theme.palette.primary.main })}
          component="div"
        >
          <Box
            sx={{
              fontWeight: "fontWeightBold",
              fontSize: textFontSize,
            }}
          >
            <Box className="statusText">
              <Box
                sx={{
                  fontWeight: "inherit",
                  fontSize: "inherited",
                  textAlign: "center",
                }}
              >
                <CardListCondensedItem
                  sx={{
                    "textAlign": "center",
                    "& .MuiGrid-root": {
                      width: "100%",
                    },
                  }}
                  itemLabel={status.mainDescription}
                  itemValue={
                    props.levelTwo
                      ? status.longDescription[0]
                      : status.shortDescription[0]
                  }
                />
                {status.shortDescription[1] || status.longDescription[1] ? (
                  <CardListCondensedItem
                    sx={{
                      "textAlign": "center",
                      "& .MuiGrid-root": {
                        width: "100%",
                      },
                    }}
                    itemValueId="active-status-sub-description"
                    itemValue={
                      props.levelTwo
                        ? status.longDescription[1]
                        : status.shortDescription[1]
                    }
                  />
                ) : undefined}
              </Box>
            </Box>
          </Box>
        </Typography>
      </Grid>
    </StyledGrid>
  );
}
